import { blobEquals, fnv1a as fnv1A, fnv1a24 as fnv1A24 } from "./hashing.js";
/**
 * A minimal perfect hash indexing lookup that
 */
export default class MinimalPerfectHash {
    /**
     *
     * @param gMap_ The G mapping (see minimum perfect hashing algorithm)
     * that provides the initial mapping from the first colliding hash function
     * to the secondary disambiguation hash function.
     * @param keyPrefixSum_ The mapping to the actual key buffer in the form
     * of a prefix sum table.
     * @param slotMap_ The slots in the hash mapped back to their enum values.
     * @param keyBlob_ The blob of keys indexed by the prefix sum.
     */
    constructor(gMap_, keyPrefixSum_, slotMap_, keyBlob_) {
        this.gMap_ = gMap_;
        this.keyPrefixSum_ = keyPrefixSum_;
        this.slotMap_ = slotMap_;
        this.keyBlob_ = keyBlob_;
    }
    /**
     * Iterates through this (alias for this.entries() that works with for of loop).
     *
     * @return {IterableIterator} The iterator to iterate through this.
     */
    [Symbol.iterator]() {
        return this.entries();
    }
    /**
     * Get the number of keys/slots in this.
     *
     * @return {number} The number of keys/slots.
     */
    get size() {
        return this.slotMap_.length;
    }
    /**
     * Implements the for each idiom on this map.
     *
     * @param callbackfn The callback function to call per element.
     * @param thisArg Extra arg to bind this to callback function.
     */
    forEach(callbackfn, thisArg) {
        const slotMap = this.slotMap_;
        const keyPrefixSum = this.keyPrefixSum_;
        const keyBlob = this.keyBlob_;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        thisArg ??= this;
        for (let where = 0, end = slotMap.length; where < end; ++where) {
            const slotValue = slotMap[where];
            if (slotValue >= 0) {
                const key = keyBlob.subarray(keyPrefixSum[where], keyPrefixSum[where + 1]);
                callbackfn.call(thisArg, slotValue, key, this);
            }
        }
    }
    /**
     * Get all the items in this map in the form of a lazy iterator.
     *
     * @return {IterableIterator} The lazy iterator to loop through the entries in the map.
     * @yields {[Uint8Array, number]} A key value tuple pair between slot valeus and the
     * key data.
     */
    *entries() {
        const slotMap = this.slotMap_;
        const keyPrefixSum = this.keyPrefixSum_;
        const keyBlob = this.keyBlob_;
        for (let where = 0, end = slotMap.length; where < end; ++where) {
            const slotValue = slotMap[where];
            if (slotValue >= 0) {
                const key = keyBlob.subarray(keyPrefixSum[where], keyPrefixSum[where + 1]);
                yield [key, slotValue];
            }
        }
    }
    /**
     * Get all the keys in this map in the form of a lazy iterator.
     *
     * @return {IterableIterator} The lazy iterator to loop through the keys in the map.
     * @yields {Uint8Array} A key from this map.
     */
    *keys() {
        const slotMap = this.slotMap_;
        const keyPrefixSum = this.keyPrefixSum_;
        const keyBlob = this.keyBlob_;
        for (let where = 0, end = slotMap.length; where < end; ++where) {
            const slotValue = slotMap[where];
            if (slotValue >= 0) {
                const key = keyBlob.subarray(keyPrefixSum[where], keyPrefixSum[where + 1]);
                yield key;
            }
        }
    }
    /**
     * Get all the values in this map in the form of a lazy iterator.
     *
     * @return {IterableIterator} The lazy iterator to loop through the values in the map.
     * @yields {number} A value from this map.
     */
    *values() {
        const slotMap = this.slotMap_;
        for (let where = 0, end = slotMap.length; where < end; ++where) {
            const slotValue = slotMap[where];
            if (slotValue >= 0) {
                yield slotValue;
            }
        }
    }
    /**
     * Does this map have a particular key.
     *
     * @param buffer The buffer to get the key to match from.
     * @param offset An optional offset into the buffer (will be 0 if not specified)
     * @param end The end offset in the buffer (non inclusive, will be length if specified.
     * @return {boolean} True if the key is in the map.
     */
    has(buffer, offset, end) {
        return this.get(buffer, offset, end) !== void 0;
    }
    /**
     * Get the corresponding value in this map for a particular key.
     *
     * @param buffer The buffer to get the key to match from.
     * @param offset An optional offset into the buffer (will be 0 if not specified)
     * @param end The end offset in the buffer (non inclusive, will be length if specified.
     * @return {number | undefined} The matching value if the key is in the map,
     * otherwise undefined.
     */
    get(buffer, offset, end) {
        offset ??= 0;
        end ??= buffer.length;
        const gMap = this.gMap_;
        const ghash = fnv1A(buffer, offset, end) % gMap.length;
        const d = gMap[ghash];
        const slotMap = this.slotMap_;
        let slot;
        if (d > 0) {
            slot = fnv1A24(buffer, offset, end, d) % slotMap.length;
        }
        else {
            slot = Math.abs(d) | 0;
        }
        if (slot < 0) {
            return;
        }
        const keyPrefixSum = this.keyPrefixSum_;
        if (!blobEquals(buffer, offset, end, this.keyBlob_, keyPrefixSum[slot], keyPrefixSum[slot + 1])) {
            return;
        }
        return this.slotMap_[slot];
    }
}
